/**
 * Plays (docs/portfolio-ux.md + docs/history-ux.md, "Revision: plays") — pure, side-effect-free,
 * unit-tested in plays.test.ts. Every order carries an `opportunity_id`; a plain ticket order is
 * its own one-leg `manual` opportunity. A **play** is an opportunity with 2+ legs: an arb pair, a
 * market making quote, a bet with a hedge (or a Get out) joined to it.
 *
 *   collectPlays     open order rows (+ the open opportunities' full `legs[]`, which still carry
 *                    the cancelled legs) → one Play per opportunity with 2+ legs
 *   buildPlay        a play's legs → its kind (Arb / Market making / Hedged / Play), the payout
 *                    per outcome ("if Hawks win +$1.20 · if Heat win +$1.20") and its status
 *   classifyPlay     working · exposed · balanced · open · settled · closed. Exposed = fills that
 *                    leave one outcome short (some legs filled, the other side didn't), the same
 *                    reading as Market making's "Exposed" chip
 *   settledItems     History's Bets / Plays toggle: settled legs grouped by opportunity into one
 *                    row with the net P&L (Σ `net_winnings − amount`)
 *
 * `leg_number` restarts on every request (FRICTION.md), so legs are ordered by `created_at`.
 */
import { betProfit } from "./history.js";
import { BALANCE_TOL, isResting, legCost, shortName, shortSide, sortLegs } from "./marketMaking.js";
import { positionHashOf, shortOutcome, type OrderLike } from "./portfolio.js";

/* ===================================================================== types */

/** An order row (or opportunity leg) with the opportunity fields plays read. */
export interface PlayLegLike extends OrderLike {
  opportunity_id?: string | null;
  opportunity_type?: string | null;
  leg_number?: number | null;
  fee?: number | null;
  funding?: { currency?: string | null } | null;
}

/** The fields of a `GET /auth/opportunities` row plays read. */
export interface OppLike<L extends PlayLegLike = PlayLegLike> {
  opportunity_id: string;
  opportunity_type?: string | null;
  status?: string | null;
  contest_id?: string | null;
  funding?: { currency?: string | null } | null;
  legs?: L[] | null;
}

export type PlayKind = "arb" | "mm" | "hedged" | "play";
export const PLAY_LABEL: Record<PlayKind, string> = { arb: "Arb", mm: "Market making", hedged: "Hedged", play: "Play" };

export type PlayStatus = "working" | "exposed" | "balanced" | "open" | "settled" | "closed";

/** One outcome of a play: if it happens, the play pays `payout` (its contracts − the total cost). */
export interface PlayOutcome {
  key: string;
  /** "Hawks win", "Under 220.5", "Timberwolves +14.5", "otherwise". */
  label: string;
  /** Filled contracts that pay if this outcome happens. */
  contracts: number;
  payout: number;
}

export interface Play<L extends PlayLegLike = PlayLegLike> {
  id: string;
  contestId: string | null;
  /** "USD" / "ATLAS" (from the legs). */
  ccy: string;
  kind: PlayKind;
  /** The API's `opportunity_type` (manual, auto_market_making, …). */
  type: string | null;
  status: PlayStatus;
  exposed: boolean;
  /** Oldest first. */
  legs: L[];
  /** Payout per outcome, or null when the legs aren't two sides of one market. */
  outcomes: PlayOutcome[] | null;
  /** Filled cost, fees included. */
  cost: number;
  filled: number;
  resting: number;
  /** Legs that ended with nothing filled (cancelled, failed, expired). */
  dead: number;
}

/* ===================================================================== legs */

const norm = (s: string | null | undefined): string => (s ?? "").trim().toLowerCase();
const tsOf = (iso: string | null | undefined): number => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? t : 0;
};
const legTs = (l: PlayLegLike): number => tsOf(l.created_at ?? l.placed_datetime);

export type LegState = "filled" | "resting" | "dead";

/** Filled (holds contracts), still resting, or dead (ended with nothing filled). */
export function legState(l: PlayLegLike): LegState {
  if (legCost(l).contracts > 0) return "filled";
  return isResting(l) ? "resting" : "dead";
}

/** The outcome a leg pays on: its venue-agnostic position, else market + side + title. */
export function outcomeKey(l: PlayLegLike): string {
  return positionHashOf(l) ?? `${l.market_id ?? ""}|${norm(l.side)}|${norm(l.title)}`;
}

const COMPLEMENT: Record<string, string> = { home: "away", away: "home", over: "under", under: "over", yes: "no", no: "yes" };

/** Two-word team nicknames whose last word alone would collide or read wrong. */
const TWO_WORD = /\b(Red Sox|White Sox|Blue Jays|Trail Blazers|Maple Leafs|Golden Knights|Blue Jackets|Red Wings)$/i;
const nick = (name: string): string => TWO_WORD.exec(name.trim())?.[1] ?? shortName(name);

/**
 * How an outcome reads after "if": "Atlanta Hawks to win Full Game" → "Hawks win", "New York
 * Knicks will win" → "Knicks win", "Minnesota Timberwolves +14.5 Full Game" → "Timberwolves +14.5",
 * "Under +241.5 Full Game" → "Under 241.5".
 */
export function outcomeLabel(title: string | null | undefined): string {
  if (!title) return "—";
  const t = title.trim().replace(/\s+Full Game$/i, "");
  const win = /^(.*?)\s+(?:to win|will win)$/i.exec(t);
  if (win) return `${nick(win[1])} win`;
  const s = shortOutcome(t);
  if (/\sML$/.test(s)) return `${nick(s.replace(/\s+ML$/, ""))} win`;
  return /^(over|under)\b/i.test(s) ? s : shortSide(s);
}

/* ================================================================== outcomes */

interface Pos {
  key: string;
  title: string | null;
  side: string;
  market: string | null;
}

/** Do two positions cover both sides of one two-way market (home/away, over/under, yes/no, or yes on each of two names)? */
function complementary(a: Pos, b: Pos): boolean {
  if (a.market && b.market && a.market !== b.market) return false;
  if (COMPLEMENT[a.side] === b.side) return true;
  return a.side === "yes" && b.side === "yes" && norm(a.title) !== norm(b.title);
}

/**
 * The payout per outcome: each outcome's filled contracts × 1 − everything the play cost
 * (fees included). Two sides of one market → both outcomes; one position → it and "otherwise";
 * legs across markets → null (outcomes overlap, there's no clean per-outcome number).
 */
export function playOutcomes(legs: PlayLegLike[]): { outcomes: PlayOutcome[] | null; cost: number } {
  const positions = new Map<string, Pos>();
  const contracts = new Map<string, number>();
  let cost = 0;
  for (const l of sortLegs(legs)) {
    const key = outcomeKey(l);
    if (!positions.has(key)) positions.set(key, { key, title: l.title ?? null, side: norm(l.side), market: l.market_id ?? null });
    const c = legCost(l);
    cost += c.stake + c.fee;
    contracts.set(key, (contracts.get(key) ?? 0) + c.contracts);
  }
  const ps = [...positions.values()];
  const mk = (p: Pos): PlayOutcome => {
    const n = contracts.get(p.key) ?? 0;
    return { key: p.key, label: outcomeLabel(p.title), contracts: n, payout: n - cost };
  };
  if (ps.length === 1) return { outcomes: [mk(ps[0]), { key: "otherwise", label: "otherwise", contracts: 0, payout: -cost }], cost };
  if (ps.length === 2 && complementary(ps[0], ps[1])) return { outcomes: [mk(ps[0]), mk(ps[1])], cost };
  return { outcomes: null, cost };
}

/* ==================================================================== status */

/** Contracts within this of each other count as balanced: 0.5, or 5% of the bigger side. */
export const balanceTol = (a: number, b: number): number => Math.max(BALANCE_TOL, 0.05 * Math.max(a, b));

/**
 * A play's status from its legs:
 *   settled   every filled leg has a result
 *   working   nothing filled yet, something resting
 *   closed    nothing filled, nothing resting
 *   exposed   filled, unsettled, and one outcome is short: two sides of a market with uneven
 *             contracts (one side filled, the other resting or cancelled), or — legs across
 *             markets — some legs filled while others didn't
 *   balanced  two sides of a market, filled evenly (a locked arb, a balanced quote, a full hedge)
 *   open      filled on one position (more of the same bet across venues): nothing to offset
 */
export function classifyPlay(legs: PlayLegLike[], outcomes: PlayOutcome[] | null = playOutcomes(legs).outcomes): PlayStatus {
  const states = legs.map(legState);
  const filledLegs = legs.filter((_, i) => states[i] === "filled");
  if (filledLegs.length === 0) return states.includes("resting") ? "working" : "closed";
  if (filledLegs.every((l) => !!l.result)) return "settled";
  if (filledLegs.some((l) => !!l.result)) return "open"; // the game's decided; nothing left to offset
  if (outcomes && outcomes.length === 2 && outcomes[1].key !== "otherwise") {
    const [a, b] = [outcomes[0].contracts, outcomes[1].contracts];
    return Math.abs(a - b) < balanceTol(a, b) ? "balanced" : "exposed";
  }
  if (!outcomes) return filledLegs.length < legs.length ? "exposed" : "open";
  return "open";
}

/**
 * The Opportunities page's old reading, kept for anything that only has leg counts: 2+ legs,
 * some filled and some not.
 */
export function isPartial(o: { legs?: PlayLegLike[] | null }): boolean {
  const legs = o.legs ?? [];
  if (legs.length < 2) return false;
  const filled = legs.filter((l) => norm(l.fill_status) === "filled" || (l.filled_amt ?? 0) > 0).length;
  return filled > 0 && filled < legs.length;
}

/* ====================================================================== kind */

/** Legs placed within this of the first leg count as one placement (one multi-leg buy). */
export const BATCH_MS = 5_000;

/**
 * Arb / Market making / Hedged / Play. Market making: greenbook's MM registry has it, or the API
 * typed it `*market_making`, or its first placement quoted both sides with limit orders. Arb: an
 * `*arbitrage` type, or both sides bought in one placement at market. Hedged: the first placement
 * took one side and a later leg bought the other. Play: anything else (more of one bet).
 */
export function playKind(legs: PlayLegLike[], opts: { mm?: boolean } = {}): PlayKind {
  if (opts.mm) return "mm";
  const types = legs.map((l) => norm(l.opportunity_type));
  if (types.some((t) => t.includes("market_making"))) return "mm";
  if (types.some((t) => t.includes("arb"))) return "arb";
  const sorted = sortLegs(legs);
  if (sorted.length === 0) return "play";
  const first = legTs(sorted[0]);
  const batch = sorted.filter((l) => legTs(l) - first <= BATCH_MS);
  if (new Set(batch.map(outcomeKey)).size >= 2) return batch.every((l) => norm(l.order_type) === "limit") ? "mm" : "arb";
  return new Set(sorted.map(outcomeKey)).size >= 2 ? "hedged" : "play";
}

/* ===================================================================== build */

const ccyOf = (l: PlayLegLike): string => (l.currency ?? l.funding?.currency ?? "USD").toUpperCase();

export function buildPlay<L extends PlayLegLike>(id: string, legs: L[], opts: { mm?: boolean; type?: string | null } = {}): Play<L> {
  const sorted = sortLegs(legs);
  const { outcomes, cost } = playOutcomes(sorted);
  const status = classifyPlay(sorted, outcomes);
  const states = sorted.map(legState);
  return {
    id,
    contestId: sorted.find((l) => l.contest_id)?.contest_id ?? null,
    ccy: sorted[0] ? ccyOf(sorted[0]) : "USD",
    kind: playKind(sorted, { mm: opts.mm }),
    type: opts.type ?? sorted.find((l) => l.opportunity_type)?.opportunity_type ?? null,
    status,
    exposed: status === "exposed",
    legs: sorted,
    outcomes,
    cost,
    filled: states.filter((s) => s === "filled").length,
    resting: states.filter((s) => s === "resting").length,
    dead: states.filter((s) => s === "dead").length,
  };
}

/**
 * The plays behind a set of open order rows: rows grouped by `opportunity_id`, merged with the
 * full `legs[]` of the matching opportunities in `opps` (the open-orders list drops cancelled
 * legs, which decide whether a quote is exposed). Only opportunities with an order in `orders`
 * and 2+ legs come back. Order rows win over an opportunity's copy of the same leg.
 */
export function collectPlays<L extends PlayLegLike>(
  orders: L[],
  opps: OppLike<L>[] = [],
  opts: { mmIds?: ReadonlySet<string> } = {},
): Map<string, Play<L>> {
  const groups = new Map<string, Map<string, L>>();
  for (const o of orders) {
    const id = o.opportunity_id;
    if (!id) continue;
    let g = groups.get(id);
    if (!g) groups.set(id, (g = new Map()));
    g.set(o.router_order_id, o);
  }
  const types = new Map<string, string | null>();
  for (const opp of opps) {
    const g = groups.get(opp.opportunity_id);
    if (!g) continue;
    types.set(opp.opportunity_id, opp.opportunity_type ?? null);
    for (const l of opp.legs ?? []) if (!g.has(l.router_order_id)) g.set(l.router_order_id, l);
  }
  const out = new Map<string, Play<L>>();
  for (const [id, g] of groups) {
    if (g.size < 2) continue;
    out.set(id, buildPlay(id, [...g.values()], { mm: opts.mmIds?.has(id), type: types.get(id) }));
  }
  return out;
}

/** Plays straight from opportunity rows (2+ legs), e.g. for the nav badge and the Desk. */
export function playsFromOpportunities<L extends PlayLegLike>(opps: OppLike<L>[], opts: { mmIds?: ReadonlySet<string> } = {}): Play<L>[] {
  return opps
    .filter((o) => (o.legs?.length ?? 0) >= 2)
    .map((o) => buildPlay(o.opportunity_id, o.legs ?? [], { mm: opts.mmIds?.has(o.opportunity_id), type: o.opportunity_type }));
}

/** The exposed plays, newest placement first. */
export function exposedPlays<P extends Play>(plays: Iterable<P>): P[] {
  const last = (p: P) => Math.max(0, ...p.legs.map(legTs));
  return [...plays].filter((p) => p.exposed).sort((a, b) => last(b) - last(a) || a.id.localeCompare(b.id));
}

/** The short outcome a play is short on (the one paying least), for "short Heat". */
export function shortOutcomeOf(p: Pick<Play, "outcomes">): PlayOutcome | null {
  const os = p.outcomes;
  if (!os || os.length < 2) return null;
  return os.reduce((a, b) => (b.payout < a.payout ? b : a));
}

/* =================================================================== settled */

export type SettledResultKind = "win" | "lose" | "push";

export interface SettledPlay<L extends PlayLegLike = PlayLegLike> {
  id: string;
  kind: PlayKind;
  contestId: string | null;
  /** Newest first, as the list shows them. */
  legs: L[];
  /** Σ leg P&L (`net_winnings − amount`, fees in). */
  net: number;
  stake: number;
  /** By the net: won (> 0), lost (< 0), push (flat). */
  result: SettledResultKind;
  settledAt: number;
}

export type SettledItem<L extends PlayLegLike = PlayLegLike> =
  | { type: "bet"; key: string; order: L; settledAt: number }
  | { type: "play"; key: string; play: SettledPlay<L>; settledAt: number };

/** A settled play's net P&L: every leg's `net_winnings − amount` (cancelled legs add nothing). */
export function playNet(legs: PlayLegLike[]): number {
  return legs.reduce((n, l) => n + (["win", "lose", "push"].includes(norm(l.result)) ? betProfit(l) : 0), 0);
}

const resultOfNet = (n: number): SettledResultKind => (n >= 0.005 ? "win" : n <= -0.005 ? "lose" : "push");

/**
 * History's Plays view: the loaded settled rows (newest first) with every opportunity that has
 * 2+ of them folded into one play row, placed where its newest leg was. Single bets stay as
 * they are. Grouping only sees loaded rows: a play's older legs may be on a later page.
 */
export function settledItems<L extends PlayLegLike>(rows: L[], opts: { mmIds?: ReadonlySet<string> } = {}): SettledItem<L>[] {
  const byOpp = new Map<string, L[]>();
  for (const o of rows) {
    if (!o.opportunity_id) continue;
    const g = byOpp.get(o.opportunity_id);
    if (g) g.push(o);
    else byOpp.set(o.opportunity_id, [o]);
  }
  const out: SettledItem<L>[] = [];
  const placed = new Set<string>();
  for (const o of rows) {
    const id = o.opportunity_id;
    const g = id ? byOpp.get(id) : undefined;
    if (!id || !g || g.length < 2) {
      out.push({ type: "bet", key: o.router_order_id, order: o, settledAt: tsOf(o.settled_datetime) });
      continue;
    }
    if (placed.has(id)) continue;
    placed.add(id);
    const legs = [...g].sort((a, b) => tsOf(b.settled_datetime) - tsOf(a.settled_datetime));
    const net = playNet(legs);
    const settledAt = Math.max(...legs.map((l) => tsOf(l.settled_datetime)));
    out.push({
      type: "play",
      key: `play:${id}`,
      settledAt,
      play: {
        id,
        kind: playKind(legs, { mm: opts.mmIds?.has(id) }),
        contestId: legs.find((l) => l.contest_id)?.contest_id ?? null,
        legs,
        net,
        stake: legs.reduce((n, l) => n + (l.amount ?? 0), 0),
        result: resultOfNet(net),
        settledAt,
      },
    });
  }
  return out;
}

/** An item's result for the result filter (a play by its net). */
export function itemResult(it: SettledItem): string {
  return it.type === "play" ? it.play.result : norm(it.order.result);
}
